import React, { useCallback, useEffect, useState, useRef, lazy } from 'react';
import { XIcon, PlayIcon, LoaderIcon } from 'lucide-react';
interface SpotifyTrack {
  id: string;
  name: string;
  album: {
    name: string;
    images: {
      url: string;
    }[];
  };
}
interface Planet {
  id: number;
  name: string;
  description: string;
  color: string;
  size: number;
  orbit: number;
  speed: number;
  angle: number;
  category: 'interstellar' | 'colliers' | 'garbs' | 'christ' | 'other';
  spotifyPlaylist?: string;
}
const UniverseExplorer = () => {
  const [activePlanet, setActivePlanet] = useState<Planet | null>(null);
  const [isExploring, setIsExploring] = useState(false);
  const [spotifyTracks, setSpotifyTracks] = useState<SpotifyTrack[]>([]);
  const [isLoadingTracks, setIsLoadingTracks] = useState(false);
  const [spotifyError, setSpotifyError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [hoverPlanet, setHoverPlanet] = useState<Planet | null>(null);
  const universeRef = useRef<HTMLDivElement>(null);
  const planets: Planet[] = [{
    id: 1,
    name: 'FABRIC REALITY',
    description: 'The birthplace of interstellar rock music. This cosmic entity pulses with the core energy of their musical vision, blending alternative rock with spacey electronic elements.',
    color: 'bg-gradient-to-br from-pink-500 to-purple-700',
    size: 80,
    orbit: 180,
    speed: 0.0015,
    angle: 0,
    category: 'interstellar',
    spotifyPlaylist: '1eBlIKgOfnWYuzUVRNpmNT'
  }, {
    id: 2,
    name: 'THE COLLIERS',
    description: 'A vibrant celestial body representing a Pop Rock Band whose musical partnerships have shaped the Fabric Reality sound over time.',
    color: 'bg-gradient-to-br from-blue-400 to-indigo-600',
    size: 60,
    orbit: 280,
    speed: 0.0012,
    angle: 2,
    category: 'colliers'
  }, {
    id: 3,
    name: 'GARBS DU ROI',
    description: 'The rhythmic heart of the Fabric Reality universe, where Lo-Fi soundscapes and lyrical inspirations are forged by Garbs, one of the founding members.',
    color: 'bg-gradient-to-br from-red-500 to-orange-500',
    size: 70,
    orbit: 380,
    speed: 0.001,
    angle: 4,
    category: 'garbs'
  }, {
    id: 4,
    name: 'CHRIST BEARER',
    description: 'A distant world of Metal music with powerful riffs and intense energy that represents the heavier elements of the Fabric Reality universe.',
    color: 'bg-gradient-to-br from-green-400 to-teal-500',
    size: 50,
    orbit: 450,
    speed: 0.0018,
    angle: 5,
    category: 'christ'
  }, {
    id: 5,
    name: 'DELOREAN NIGHTS',
    description: 'A nostalgic realm of Pure Synthwave, where retro electronic sounds and futuristic melodies transport listeners to a neon-lit alternate dimension.',
    color: 'bg-gradient-to-br from-purple-400 to-indigo-700',
    size: 65,
    orbit: 320,
    speed: 0.0012,
    angle: 3,
    category: 'other'
  }];
  // Check for mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  // Function to fetch Spotify tracks
  const fetchSpotifyTracks = useCallback(async (artistId: string) => {
    setIsLoadingTracks(true);
    setSpotifyError(null);
    try {
      // For simplicity and security, we'll use the Spotify embed approach instead of direct API calls
      // This avoids exposing credentials in client-side code
      setSpotifyTracks([]);
      // The tracks will be loaded directly via the Spotify iframe embed
    } catch (error) {
      console.error('Error fetching Spotify tracks:', error);
      setSpotifyError('Failed to load tracks. Please try again later.');
    } finally {
      setIsLoadingTracks(false);
    }
  }, []);
  useEffect(() => {
    let animationFrameId: number;
    let planetElements: NodeListOf<Element>;
    const animatePlanets = () => {
      planets.forEach((planet, index) => {
        planet.angle += planet.speed;
        if (planet.angle >= Math.PI * 2) {
          planet.angle = 0;
        }
        if (planetElements && planetElements[index]) {
          const x = Math.cos(planet.angle) * planet.orbit;
          const y = Math.sin(planet.angle) * planet.orbit;
          (planetElements[index] as HTMLElement).style.transform = `translate(${x}px, ${y}px)`;
        }
      });
      animationFrameId = requestAnimationFrame(animatePlanets);
    };
    if (isExploring && universeRef.current) {
      planetElements = universeRef.current.querySelectorAll('.planet');
      animationFrameId = requestAnimationFrame(animatePlanets);
    }
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isExploring]);
  const handlePlanetClick = (planet: Planet) => {
    setActivePlanet(planet);
    // If it's the interstellar rock planet, fetch tracks
    if (planet.category === 'interstellar' && planet.spotifyPlaylist) {
      fetchSpotifyTracks(planet.spotifyPlaylist);
    }
  };
  const handlePlanetHover = (planet: Planet | null) => {
    // Use a functional update to avoid potential issues with stale closures
    setHoverPlanet(current => planet);
  };
  const startExploring = () => {
    setIsExploring(true);
  };
  // Mobile-specific planet selector
  const MobilePlanetSelector = () => {
    return <div className="bg-black/90 backdrop-blur-md p-4 rounded-lg border border-purple-500/30 mb-4" data-id="element-627">
        <h3 className="text-lg font-semibold text-white mb-3" data-id="element-628">
          Select a planet:
        </h3>
        <div className="grid grid-cols-1 gap-3" data-id="element-629">
          {planets.map(planet => <button key={planet.id} onClick={() => handlePlanetClick(planet)} className={`p-3 rounded-lg border ${activePlanet?.id === planet.id ? 'border-pink-500 shadow-lg shadow-pink-500/20' : 'border-gray-700'} transition-all`} data-id="element-630">
              <div className="flex items-center" data-id="element-631">
                <div className={`w-8 h-8 rounded-full ${planet.color} mr-3 flex-shrink-0`} data-id="element-632"></div>
                <span className="text-sm font-medium text-white" data-id="element-633">
                  {planet.name}
                </span>
              </div>
            </button>)}
        </div>
      </div>;
  };
  const getCategoryContent = (planet: Planet) => {
    switch (planet.category) {
      case 'interstellar':
        return <div className="mt-4 space-y-4" data-id="element-634">
            <h4 className="text-lg font-semibold text-pink-400 mb-2" data-id="element-635">
              Interstellar Rock Tracks:
            </h4>
            {isLoadingTracks ? <div className="flex items-center justify-center p-8" data-id="element-636">
                <LoaderIcon size={24} className="text-pink-400 animate-spin" data-id="element-637" />
                <span className="ml-2 text-gray-300" data-id="element-638">Loading tracks...</span>
              </div> : spotifyError ? <div className="bg-red-900/20 p-4 rounded-lg text-red-300" data-id="element-639">
                {spotifyError}
              </div> : <div className="bg-black/40 rounded-lg p-4 border border-pink-500/20" data-id="element-640">
                <iframe src={`https://open.spotify.com/embed/artist/${planet.spotifyPlaylist}?utm_source=generator&theme=0`} width="100%" height={isMobile ? '152' : '352'} frameBorder="0" allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy" title="Fabric Reality Spotify Tracks" className="rounded-lg" data-id="element-641"></iframe>
              </div>}
          </div>;
      case 'colliers':
        return <div className="mt-4" data-id="element-642">
            <h4 className="text-lg font-semibold text-blue-400 mb-2" data-id="element-643">
              The Colliers Experience:
            </h4>
            <p className="text-gray-300 mb-4" data-id="element-644">
              The Colliers represents the collaborative spirit of Fabric
              Reality, bringing together diverse musical influences and creative
              partnerships that have shaped their unique sound.
            </p>
            <div className="bg-blue-900/20 p-4 rounded-lg border border-blue-500/20" data-id="element-645">
              <p className="text-blue-300 italic" data-id="element-646">
                "Our collaborations have been essential to developing the Fabric
                Reality sound. Each partnership brings new dimensions to our
                cosmic universe."
              </p>
            </div>
          </div>;
      case 'garbs':
        return <div className="mt-4" data-id="element-647">
            <h4 className="text-lg font-semibold text-red-400 mb-2" data-id="element-648">
              Garbs Du Roi's Realm:
            </h4>
            <p className="text-gray-300 mb-4" data-id="element-649">
              Garbs Du Roi represents the lyrical and vocal heart of Fabric
              Reality, where powerful storytelling and emotional depth create
              the human connection within the cosmic soundscape.
            </p>
            <div className="bg-red-900/20 p-4 rounded-lg border border-red-500/20" data-id="element-650">
              <p className="text-red-300 italic" data-id="element-651">
                "The lyrics and melodies are our way of translating cosmic
                experiences into human emotions. This planet represents that
                translation process."
              </p>
            </div>
          </div>;
      case 'christ':
        return <div className="mt-4" data-id="element-652">
            <h4 className="text-lg font-semibold text-green-400 mb-2" data-id="element-653">
              Christ Bearer's Laboratory:
            </h4>
            <p className="text-gray-300 mb-4" data-id="element-654">
              Christ Bearer symbolizes the technical and production wizardry
              behind Fabric Reality, where sound design and electronic elements
              are crafted to create the signature interstellar sound.
            </p>
            <div className="bg-green-900/20 p-4 rounded-lg border border-green-500/20" data-id="element-655">
              <p className="text-green-300 italic" data-id="element-656">
                "The production techniques we use are like scientific
                experiments - combining different elements to create something
                that feels both familiar and completely new."
              </p>
            </div>
          </div>;
      default:
        return <div className="mt-4" data-id="element-657">
            <h4 className="text-lg font-semibold text-pink-400 mb-2" data-id="element-658">
              Explore This Planet:
            </h4>
            <p className="text-gray-300" data-id="element-659">
              This cosmic body is part of the expanding Fabric Reality universe.
              Check back for more content as our universe continues to grow.
            </p>
          </div>;
    }
  };
  return <section className="pt-28 md:pt-36 pb-16 w-full" data-id="element-660">
      <div className="max-w-6xl mx-auto px-4" data-id="element-661">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-8 md:mb-12 font-mono text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-400 inline-block" data-id="element-662">
          UNIVERSE EXPLORER
        </h2>
        {!isExploring ? <div className="bg-black/60 backdrop-blur-sm p-6 md:p-8 rounded-lg border border-indigo-500/30 shadow-lg shadow-indigo-500/20 text-center" data-id="element-663">
            <h3 className="text-2xl md:text-3xl font-semibold text-white mb-4 md:mb-6" data-id="element-664">
              Enter The Fabric Reality Universe
            </h3>
            <p className="text-gray-300 mb-6 md:mb-8 max-w-2xl mx-auto" data-id="element-665">
              Explore the cosmic worlds that inspire Fabric Reality's music.
              Each planet represents a different aspect of their sonic universe,
              with unique tracks and visual experiences.
            </p>
            <div className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-8 md:mb-10" data-id="element-666">
              <div className="bg-purple-900/20 p-4 md:p-6 rounded-lg border border-purple-500/20" data-id="element-667">
                <h4 className="text-lg md:text-xl text-pink-300 mb-2 md:mb-3" data-id="element-668">
                  Discover New Sounds
                </h4>
                <p className="text-gray-300" data-id="element-669">
                  Each planet contains exclusive content that represents
                  different facets of Fabric Reality's musical journey.
                </p>
              </div>
              <div className="bg-purple-900/20 p-4 md:p-6 rounded-lg border border-purple-500/20" data-id="element-670">
                <h4 className="text-lg md:text-xl text-pink-300 mb-2 md:mb-3" data-id="element-671">
                  Interactive Experience
                </h4>
                <p className="text-gray-300" data-id="element-672">
                  Navigate through our cosmic universe and interact with
                  celestial bodies that pulse with music.
                </p>
              </div>
            </div>
            <button onClick={startExploring} className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-pink-600 to-purple-600 text-white font-bold text-lg rounded-lg transition-all duration-300 hover:from-pink-500 hover:to-purple-500 hover:shadow-lg hover:shadow-pink-500/30 transform hover:scale-105" data-id="element-673">
              BEGIN EXPLORATION
            </button>
          </div> : <div className="relative h-[500px] sm:h-[600px] md:h-[700px] overflow-hidden bg-black/60 backdrop-blur-sm rounded-lg border border-indigo-500/30 shadow-lg shadow-indigo-500/20" data-id="element-674">
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1506318137071-a8e063b4bec0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=3540&q=80')] bg-cover bg-center opacity-30" data-id="element-675"></div>
            {/* Header */}
            <div className="absolute top-4 left-4 right-4 flex flex-col sm:flex-row justify-between items-start sm:items-center z-40" data-id="element-676">
              <h3 className="text-xl sm:text-2xl font-semibold text-white mb-2 sm:mb-0" data-id="element-677">
                <span className="text-pink-400" data-id="element-678">FABRIC REALITY</span> UNIVERSE
              </h3>
              {!isMobile && <p className="text-xs sm:text-sm text-gray-300 font-mono bg-black/50 px-3 py-1 rounded-full" data-id="element-679">
                  Hover over a planet to see details
                </p>}
            </div>
            {/* Mobile planet selector */}
            {isMobile && isExploring && !activePlanet && <div className="absolute top-16 left-4 right-4 z-40" data-id="element-680">
                <MobilePlanetSelector data-id="element-681" />
              </div>}
            {/* Universe container */}
            <div ref={universeRef} className="absolute inset-0 flex items-center justify-center" data-id="element-682">
              {/* Sun/Center */}
              <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-r from-yellow-300 to-orange-500 shadow-lg shadow-orange-500/50 animate-pulse z-10 relative" data-id="element-683">
                <div className="absolute -inset-4 bg-orange-500/20 rounded-full animate-ping opacity-70" data-id="element-684"></div>
                <div className="absolute -inset-8 bg-orange-500/10 rounded-full" data-id="element-685"></div>
              </div>
              {/* Orbit paths - Hide on mobile */}
              {!isMobile && planets.map(planet => <div key={`orbit-${planet.id}`} className="absolute rounded-full border border-white/10" style={{
            width: planet.orbit * 2,
            height: planet.orbit * 2
          }} data-id="element-686" />)}
              {/* Planets - Only show on desktop or if no planet is selected on mobile */}
              {(!isMobile || !activePlanet) && planets.map(planet => <div key={planet.id} className={`planet absolute ${planet.color} rounded-full cursor-pointer hover:shadow-lg hover:shadow-pink-500/50 transition-shadow duration-300 z-20`} style={{
            width: `${isMobile ? planet.size * 0.8 : planet.size}px`,
            height: `${isMobile ? planet.size * 0.8 : planet.size}px`,
            transform: `translate(${Math.cos(planet.angle) * (isMobile ? planet.orbit * 0.6 : planet.orbit)}px, ${Math.sin(planet.angle) * (isMobile ? planet.orbit * 0.6 : planet.orbit)}px)`
          }} onClick={() => handlePlanetClick(planet)} onMouseEnter={() => handlePlanetHover(planet)} onMouseLeave={() => handlePlanetHover(null)} data-id="element-687">
                    <div className="absolute -inset-2 bg-white/10 rounded-full opacity-0 hover:opacity-70 transition-opacity" data-id="element-688"></div>
                    {isMobile && <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-[10px] text-white bg-black/90 px-2 py-0.5 rounded-full whitespace-nowrap max-w-[90px] truncate" data-id="element-689">
                        {planet.name}
                      </div>}
                    {/* Planet Tooltip - Only shown on hover */}
                    {!isMobile && hoverPlanet && hoverPlanet.id === planet.id && <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-4 w-64 bg-black/90 backdrop-blur-md p-3 rounded-lg border border-pink-500/30 z-50 shadow-lg shadow-pink-500/20 pointer-events-none animate-fade-in" data-id="element-690">
                          <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-black/90 border-t border-l border-pink-500/30 rotate-45" data-id="element-691"></div>
                          <h4 className="text-lg font-bold text-white mb-1" data-id="element-692">
                            {planet.name}
                          </h4>
                          <p className="text-xs text-gray-300" data-id="element-693">
                            {planet.description.split('.')[0]}.
                          </p>
                          <div className="mt-2 text-xs text-pink-400" data-id="element-694">
                            Click to explore
                          </div>
                        </div>}
                  </div>)}
            </div>
            {/* Planet detail panel */}
            {activePlanet && <div className={`absolute ${isMobile ? 'inset-0 bg-black/90' : 'bottom-0 left-0 right-0 bg-black/80'} backdrop-blur-md p-4 sm:p-6 ${isMobile ? 'border-0' : 'border-t border-pink-500/30'} max-h-${isMobile ? '100%' : '[60%] sm:max-h-[70%]'} overflow-y-auto z-30`} data-id="element-695">
                <div className="flex items-start" data-id="element-696">
                  <div className={`w-10 h-10 sm:w-16 sm:h-16 rounded-full ${activePlanet.color} mr-3 sm:mr-4 flex-shrink-0`} data-id="element-697"></div>
                  <div className="flex-grow" data-id="element-698">
                    <h3 className="text-xl sm:text-2xl font-bold text-white mb-2" data-id="element-699">
                      {activePlanet.name}
                    </h3>
                    <p className="text-sm sm:text-base text-gray-300 mb-4" data-id="element-700">
                      {activePlanet.description}
                    </p>
                    {getCategoryContent(activePlanet)}
                  </div>
                  <button onClick={() => setActivePlanet(null)} className="ml-2 sm:ml-4 p-2 rounded-full bg-gray-800 hover:bg-gray-700 text-white" data-id="element-701">
                    <XIcon size={20} data-id="element-702" />
                  </button>
                </div>
              </div>}
          </div>}
      </div>
      <style jsx data-id="element-703">{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translate(-50%, 10px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.2s ease-out forwards;
        }
      `}</style>
    </section>;
};
export default UniverseExplorer;